import axios from 'axios';

const resolveApiUrl = () => {
  const normalizedEnvApiUrl = (process.env.REACT_APP_API_URL || '').trim();

  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location;
    const envApiUrl = normalizedEnvApiUrl;
    const envPointsToLocalhost =
      !!envApiUrl &&
      (envApiUrl.includes('127.0.0.1') || envApiUrl.includes('localhost'));

    if (envApiUrl && !(envPointsToLocalhost && hostname !== 'localhost' && hostname !== '127.0.0.1')) {
      return envApiUrl;
    }

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://127.0.0.1:8000/api';
    }
    return `${origin}/api`;
  }

  if (normalizedEnvApiUrl) {
    return normalizedEnvApiUrl;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_URL = resolveApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

const SCOPED_ENDPOINT_PATTERNS = [
  /^\/menu-items(?:\/|$)/,
  /^\/orders(?:\/|$)/,
  /^\/bookings(?:\/|$)/,
  /^\/payments(?:\/|$)/,
  /^\/dashboard(?:\/|$)/,
];

const parseRestaurantId = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const getRestaurantIdFromLocation = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const queryRestaurantId = parseRestaurantId(new URLSearchParams(window.location.search).get('restaurant_id'));
  if (queryRestaurantId) {
    return queryRestaurantId;
  }

  const pathMatch = window.location.pathname.match(/^\/(?:menu|admin)\/(\d+)(?:\/|$)/);
  return parseRestaurantId(pathMatch?.[1]);
};

const shouldScopeByRestaurant = (url = '') => {
  const path = url.startsWith('http') ? new URL(url).pathname.replace('/api', '') : url;
  return SCOPED_ENDPOINT_PATTERNS.some((pattern) => pattern.test(path));
};

const resolveScopedRestaurantId = (userOverride = null) => {
  const user = userOverride || getStoredUser();
  const locationRestaurantId = getRestaurantIdFromLocation();
  const savedRestaurantId = parseRestaurantId(localStorage.getItem('selected_restaurant_id'));

  if (user?.role === 'staff') {
    return parseRestaurantId(user.restaurant_id);
  }

  // Admins can explicitly choose a restaurant; if none is chosen, allow unscoped queries.
  if (user?.role === 'admin') {
    if (locationRestaurantId) {
      return locationRestaurantId;
    }

    if (savedRestaurantId) {
      return savedRestaurantId;
    }

    return null;
  }

  if (locationRestaurantId) {
    return locationRestaurantId;
  }

  if (savedRestaurantId) {
    return savedRestaurantId;
  }

  if (!user || user?.role === 'customer') {
    return 1;
  }

  return null;
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (shouldScopeByRestaurant(config.url || '')) {
      const existingRestaurantId = parseRestaurantId(config.params?.restaurant_id);
      if (!existingRestaurantId) {
        const scopedRestaurantId = resolveScopedRestaurantId();
        if (scopedRestaurantId) {
          config.params = {
            ...(config.params || {}),
            restaurant_id: scopedRestaurantId,
          };
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication endpoints
export const authAPI = {
  // Public endpoints
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  
  // Protected endpoints
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  
  // Admin only
  createStaff: (data) => api.post('/auth/staff/create', data),
};

// Menu Items endpoints
export const menuAPI = {
  getAll: (params) => api.get('/menu-items', { params }),
  getById: (id) => api.get(`/menu-items/${id}`),
  create: (data) => api.post('/menu-items', data),
  update: (id, data) => api.put(`/menu-items/${id}`, data),
  delete: (id) => api.delete(`/menu-items/${id}`),
};

// Orders endpoints
export const orderAPI = {
  getAll: (params) => api.get('/orders', { params }), // Admin/Staff only
  getMyOrders: (params) => api.get('/my-orders', { params }), // Customer's own orders
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  updateStatus: (id, status) => api.put(`/orders/${id}`, { status }),
  delete: (id) => api.delete(`/orders/${id}`),
};

// Bookings endpoints
export const bookingAPI = {
  getAll: (params) => api.get('/bookings', { params }), // Admin/Staff only
  getMyBookings: (params) => api.get('/my-bookings', { params }), // Customer's own bookings
  getBookedDates: (params) => api.get('/bookings/booked-dates', { params }), // Get all booked dates for calendar
  getById: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  updateStatus: (id, status) => api.put(`/bookings/${id}`, { status }),
  delete: (id) => api.delete(`/bookings/${id}`),
};

// Event Bookings endpoints
export const eventBookingAPI = {
  getAll: (params) => api.get('/event-bookings', { params }), // Admin/Staff only
  getMyEventBookings: (params) => api.get('/my-event-bookings', { params }), // Customer's own event bookings
  getBookedDates: () => api.get('/event-bookings/booked-dates'), // Get all booked dates for calendar
  getById: (id) => api.get(`/event-bookings/${id}`),
  create: (data) => api.post('/event-bookings', data),
  update: (id, data) => api.put(`/event-bookings/${id}`, data),
  updateStatus: (id, status) => api.put(`/event-bookings/${id}`, { status }),
  delete: (id) => api.delete(`/event-bookings/${id}`),
};

// Payments endpoints
export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }), // Admin/Staff only
  getMyPayments: (params) => api.get('/my-payments', { params }), // Customer's own payments
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
};

// Feedback endpoints
export const feedbackAPI = {
  getAll: (params) => api.get('/feedback', { params }), // Admin/Staff only
  getMyFeedback: (params) => api.get('/my-feedback', { params }), // Customer's own feedback
  getById: (id) => api.get(`/feedback/${id}`),
  create: (data) => api.post('/feedback', data),
  delete: (id) => api.delete(`/feedback/${id}`),
};

// Reports endpoints
export const reportAPI = {
  getSummary: (params) => api.get('/reports/summary', { params }),
  getSales: (params) => api.get('/reports/sales', { params }),
  getBookings: (params) => api.get('/reports/bookings', { params }),
  getTopItems: (params) => api.get('/reports/top-items', { params }),
};

// Dashboard endpoint
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard', { params }),
};

// User Management endpoints (Admin only)
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export { resolveScopedRestaurantId };
export default api;